import React, {ReactNode, memo, useRef, useEffect, CSSProperties, useState} from 'react';
import cx from 'classnames';
import {Text} from '@priority-software/priority-style-react';
import {CheckedValue} from './enum';
import styles from './styles.module.scss';
import {createDataTestId} from '../../utils/dataTestId';

export type Props = {
    checked: CheckedValue;
    onChange: (checked: CheckedValue) => void;
    componentClasses?: string;
    left: ReactNode;
    right: ReactNode;
    size?: 'small' | 'medium' | 'large';
    itemWidth?: CSSProperties['width'];
    disabled?: boolean;
};

export const ToggleButton = memo(
    ({
        onChange,
        checked,
        componentClasses,
        right,
        left,
        disabled = false,
        size = 'medium',
        itemWidth = '50px'
    }: Props) => {
        const checkedLeft = checked === CheckedValue.left;
        const checkedRight = checked === CheckedValue.right;
        const ref = useRef<HTMLDivElement>(null);
        const [initialized, setInitialized] = useState(false);

        const handleToggleSide = () => {
            if (!disabled) {
                const nextValue =
                    checked === CheckedValue.left ? CheckedValue.right : CheckedValue.left;
                onChange(nextValue);
            }
        };

        useEffect(() => {
            const elementLeft =
                ref.current?.children.length > 0 ? ref.current?.children[0] : undefined;
            const elementRight =
                ref.current?.children.length > 1 ? ref.current?.children[1] : undefined;
            if (!elementLeft || !elementRight) return () => {};

            const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                for (const entry of entries) {
                    if (entry.contentBoxSize) {
                        const isLeft = entry.target === elementLeft;
                        ref.current.style.setProperty(
                            isLeft ? '--left-button-width' : '--right-button-width',
                            entry.target.clientWidth.toString()
                        );
                        setTimeout(() => setInitialized(true));
                    }
                }
            });

            resizeObserver.observe(elementLeft);
            resizeObserver.observe(elementRight);

            return () => {
                resizeObserver.unobserve(elementLeft);
                resizeObserver.unobserve(elementRight);
                resizeObserver.disconnect();
            };
        }, []);

        return (
            <div
                {...createDataTestId('toggle-button-container', false)}
                onClick={handleToggleSide}
                className={cx(styles.toggleOperator, componentClasses, {
                    [styles.small]: size === 'small',
                    [styles.medium]: size === 'medium',
                    [styles.large]: size === 'large',
                    [styles.left]: checkedLeft,
                    [styles.right]: checkedRight,
                    [styles.disabled]: disabled,
                    [styles.withTransition]: initialized
                })}
                style={
                    {
                        '--item-width': itemWidth
                    } as any
                }
                ref={ref}
            >
                <Text
                    {...createDataTestId('toggle-button-left', true)}
                    skin="paragraph-2"
                    componentClasses={cx(styles.item, {
                        [styles.activeOperator]: checked === CheckedValue.left
                    })}
                >
                    {left}
                </Text>
                <Text
                    {...createDataTestId('toggle-button-right', true)}
                    skin="paragraph-2"
                    componentClasses={cx(styles.item, {
                        [styles.activeOperator]: checked === CheckedValue.right
                    })}
                >
                    {right}
                </Text>
            </div>
        );
    },
    (prev, next) => prev.checked === next.checked
);
